"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface CustomFieldProps {
	id: string;
	label: string;
	subtype?: string;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	children?: ReactNode;
}

/**
 * Extension point for registered custom subtypes. Core `richtext` is not custom.
 */
export function CustomField({
	id,
	label,
	subtype,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	disabled = false,
	layoutHint,
	className,
	children,
}: CustomFieldProps) {
	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(disabled && "pointer-events-none opacity-50", className)}
		>
			{name ? <input type="hidden" name={name} readOnly /> : null}
			<div
				id={id}
				data-field-type="custom"
				data-custom-subtype={subtype}
				className="rounded-md border border-dashed border-input p-3 text-sm text-muted-foreground"
				aria-invalid={Boolean(error) ? true : undefined}
				onBlur={onBlur}
			>
				{children ?? `Custom field${subtype ? `: ${subtype}` : ""}`}
			</div>
		</FieldShell>
	);
}
